import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PageHead } from '@/admin-area/ui/PageHead';
import { PhonePrimary } from '@/admin-area/ui/PhonePrimary';
import { Button } from '@/components/ui/button';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { deleteRoute, listRoutes, type SavedRoute } from './api';
import { formatKm } from './calc';
import { MileageList } from './MileageList';
import { RouteForm } from './RouteForm';
import { useLoaded } from './use-loaded';

/** «Percorsi usuali»: the person's saved routes, one-way km (release 3): list, add, edit, delete. */
export function RoutesPage() {
  const { t } = useTranslation('expenses');
  const { state, reload } = useLoaded(listRoutes);
  const [editing, setEditing] = useState<SavedRoute | 'new' | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const remove = async (route: SavedRoute): Promise<void> => {
    setFailed(false);
    const outcome = await deleteRoute(route.id);
    setConfirming(null);
    if (outcome.ok) reload();
    else setFailed(true);
  };
  const add = (
    <Button type="button" onClick={() => setEditing('new')}>
      <Plus />
      {t('mileage.routes.add')}
    </Button>
  );
  return (
    <section aria-label={t('mileage.routes.title')} className="flex flex-col gap-4">
      <PageHead
        title={t('mileage.routes.title')}
        summary={t('mileage.routes.summary')}
        actions={<span className="page-primary">{add}</span>}
      />
      {failed ? <p role="alert">{t('mileage.routes.deleteFailed')}</p> : null}
      {state.status === 'loading' ? <p role="status">{t('mileage.loading')}</p> : null}
      {state.status === 'failed' ? <p role="alert">{t('mileage.loadFailed')}</p> : null}
      {state.status === 'ready' && state.items.length === 0 ? (
        <p className="text-muted-foreground">{t('mileage.routes.empty')}</p>
      ) : null}
      {state.status === 'ready' ? (
        <RouteList
          routes={state.items}
          confirming={confirming}
          onEdit={setEditing}
          onAsk={setConfirming}
          onConfirm={(route) => void remove(route)}
        />
      ) : null}
      <PhonePrimary>{add}</PhonePrimary>
      <Sheet open={editing !== null} onOpenChange={(open) => (open ? undefined : setEditing(null))}>
        <SheetContent className="flex flex-col gap-4 p-4">
          <SheetTitle>
            {editing === 'new' ? t('mileage.routes.addTitle') : t('mileage.routes.editTitle')}
          </SheetTitle>
          <SheetDescription>{t('mileage.routes.formHelp')}</SheetDescription>
          {editing === null ? null : (
            <RouteForm
              {...(editing === 'new' ? {} : { route: editing })}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                reload();
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}

function RouteActions({
  route,
  confirming,
  onEdit,
  onAsk,
  onCancel,
  onConfirm,
}: {
  readonly route: SavedRoute;
  readonly confirming: boolean;
  readonly onEdit: () => void;
  readonly onAsk: () => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const { t } = useTranslation('expenses');
  const name = `${route.from} → ${route.to}`;
  if (confirming) {
    return (
      <span className="flex items-center gap-1">
        <span className="text-sm">{t('mileage.routes.deleteAsk')}</span>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          className="max-lg:min-h-11"
          onClick={onConfirm}
        >
          {t('mileage.routes.delete')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="max-lg:min-h-11"
          onClick={onCancel}
        >
          {t('mileage.cancel')}
        </Button>
      </span>
    );
  }
  return (
    <>
      <Button asChild variant="outline" size="sm" className="max-lg:min-h-11">
        <Link
          to="/expenses/trips/new"
          state={{ route }}
          aria-label={t('mileage.routes.useFor', { name })}
        >
          {t('mileage.links.newTrip')}
        </Link>
      </Button>
      <Button type="button" variant="ghost" size="sm" className="max-lg:min-h-11" onClick={onEdit}>
        {t('mileage.edit')}
      </Button>
      <RowActionsMenu
        label={t('mileage.routes.moreFor', { name })}
        triggerClassName="max-lg:size-11"
        actions={[
          {
            id: 'delete',
            label: t('mileage.routes.delete'),
            tone: 'destructive',
            onSelect: onAsk,
          },
        ]}
      />
    </>
  );
}

function RouteList({
  routes,
  confirming,
  onEdit,
  onAsk,
  onConfirm,
}: {
  readonly routes: readonly SavedRoute[];
  readonly confirming: string | null;
  readonly onEdit: (route: SavedRoute) => void;
  readonly onAsk: (routeId: string | null) => void;
  readonly onConfirm: (route: SavedRoute) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <MileageList
      label={t('mileage.routes.title')}
      items={routes}
      rowKey={(route) => route.id}
      columns={[
        { header: t('mileage.routes.fromShort'), cell: (route) => route.from },
        { header: t('mileage.routes.toShort'), cell: (route) => route.to },
        {
          header: t('mileage.routes.km'),
          numeric: true,
          cell: (route) => `${formatKm(route.km)} km`,
        },
      ]}
      card={(route) => ({
        title: `${route.from} → ${route.to}`,
        detail: `${formatKm(route.km)} km`,
      })}
      actions={(route) => (
        <RouteActions
          route={route}
          confirming={confirming === route.id}
          onEdit={() => onEdit(route)}
          onAsk={() => onAsk(route.id)}
          onCancel={() => onAsk(null)}
          onConfirm={() => onConfirm(route)}
        />
      )}
    />
  );
}
